<template>
  <div>
    <ContentSectionLoader v-if="!loaded" />
    <Transition name="fade">
      <div v-if="loaded && post">
        <div
          v-if="post.attributes.status === 'Pending'"
          v-can="'manage-discussions'"
          class="card mb-8"
        >
          <div
            class="mt-0 flash flash--success rounded-t text-center"
            role="alert"
          >
            <strong v-text="'Heads up!'" /> This post is pending admin approval.
          </div>
          <div class="flex flex-row p-4 justify-center">
            <div
              class="text-grey-darkest mr-8"
              v-text="'Would you like to approve this discussion?'"
            />
            <div class="flex">
              <button
                type="button"
                class="btn btn--sm"
                @click="approve"
                v-text="'Approve'"
              />
              <button
                type="button"
                class="ml-6"
                @click="reject"
                v-text="'Reject'"
              />
            </div>
          </div>
        </div>
        <article class="card">
          <header class="text-center border-b pb-2 mb-4 border-grey">
            <Popper
              v-cloak
              ref="popper"
              v-can="'edit-discussions'"
              trigger="click"
              class="float-right mr-4"
              :options="{}"
            >
              <div class="popper popper-simple popper-arrow">
                <div class="max-w-md flex flex-col">
                  <div class="flex flex-col text-left">
                    <a
                      v-can="'edit-discussions'"
                      :href="editPostUrl"
                      v-text="'Edit'"
                    />
                    <a
                      v-can="'archive-discussions'"
                      @click="openArchivePostModal"
                      v-text="'Archive'"
                    />
                  </div>
                </div>
              </div>
              <a
                slot="reference"
                class="btn btn--sm btn--icon-only icon icon--small"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="15"
                  viewBox="0 0 9 15"
                >
                  <path
                    d="M7.67 2.48l.66-1.23L6.25.21 5.56 1.5zm-2.35-.54L.48 10.95l2.11.98 4.84-9.01zM.31 11.43L.1 14.11l2.21-1.75z"
                  />
                </svg>
              </a>
            </Popper>
            <div>
              <h1
                class="text-grey-darkest text-2xl"
                v-text="post.attributes.title"
              />
              <div class="text-sm m-4 ">
                <a
                  class="btn btn--sm btn--border mr-2"
                  v-text="post.category.attributes.title"
                />
                <span v-can="'manage-discussions'">
                  <span
                    class="mx-2"
                    :class="statusClass"
                    v-text="post.attributes.status"
                  />
                </span>
                <span
                  class="mx-2 "
                  v-text="repliesCount"
                />
              </div>
            </div>
          </header>
          <div class="card-content mx-auto pt-0 flex-row">
            <div class="flex-1">
              <div class="flex justify-between">
                <div class="flex flex-1 flex-col sm:flex-row items-center justify-between">
                  <div class="flex items-center">
                    <img
                      :src="authorAvatar"
                      alt=""
                      class="rounded-full mr-4 w-8 h-8"
                    >
                    <span v-html="post.creator.attributes.full_name" />
                    <span
                      v-if="creatorRole"
                      class="ml-4 uppercase border border-grey-darker text-grey-darker  inline-block px-1 text-xs rounded"
                      v-text="creatorRole"
                    />
                  </div>
                  <div class="pl-8 text-grey-darker">
                    {{ post.attributes.created_at | calendarTime }}
                  </div>
                </div>
              </div>
              <div
                class="pt-4 text-grey-darkest"
                v-html="post.attributes.body"
              />
            </div>
          </div>
          <div v-if="documents.length">
            <hr>
            <table
              class="table table-files min-w-table"
              style="min-height: auto;"
            >
              <thead>
                <tr>
                  <th />
                  <th v-text="'Name'" />
                  <th v-text="'Modified'" />
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  is="DocumentRow"
                  v-for="document in documents"
                  :key="document.uuid"
                  :show-actions="false"
                  :show-shares="false"
                  :show-visibilities="false"
                  :document="document"
                />
              </tbody>
            </table>
          </div>
          <footer
            v-can="'create-discussion-replies'"
            class="card-footer text-center flex pb-4 px-0"
          >
            <div
              v-if="isReplying"
              class="card-content pt-0 flex-1 flex flex-row mx-auto"
            >
              <div class="flex-1 flex-col">
                <Redactor
                  v-model="reply.body"
                  class="input text-lg pb-2"
                  :config="redactorConfig"
                />
                <div class="flex justify-between items-center">
                  <div>
                    <span
                      class="btn btn--md px-10"
                      :class="{ 'btn--disabled': !reply.body.length }"
                      @click="submit"
                      v-text="'Post'"
                    />
                  </div>
                  <div class="ml-6">
                    <button
                      type="button"
                      @click="cancel"
                      v-text="'Cancel'"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="!isReplying"
              class="card-content pt-0 pb-0 flex-1 flex flex-row mx-auto"
            >
              <button
                type="button"
                class="btn btn--border"
                @click.stop="toggleReply"
                v-text="'Reply'"
              />
            </div>
          </footer>
        </article>
        <div
          v-if="loaded"
          class="flex items-center justify-between content-center px-2 mt-8 mb-4"
        >
          <h2
            class="flex-1 m-0"
            v-text="`Replies (${post.meta.reply_count})`"
          />
          <div class="w-1/2 text-xs text-right">
            Sort By:
            <select
              v-model="sort_direction"
              class="w-24 mb-0 ml-2 text-xs"
            >
              <option
                value="asc"
                v-text="'Ascending'"
              />
              <option
                value="desc"
                v-text="'Descending'"
              />
            </select>
          </div>
        </div>
        <div v-if="repliesLoaded">
          <DiscussionReply
            v-for="discussionReply in replies"
            :key="discussionReply.uuid"
            :reply="discussionReply"
          />
        </div>
        <Pagination
          v-if="replyPagination"
          :pagination="replyPagination"
          :on-change="onReplyPageChange"
        />
        <ArchiveDiscussionPostModal @archivePost="archive" />
        <ArchiveDiscussionReplyModal />
      </div>
    </Transition>
  </div>
</template>

<script type="text/babel">
import Popper from 'vue-popperjs'
import { mapActions, mapState } from 'vuex'
import ArchiveDiscussionPostModal from './modals/ArchiveDiscussionPostModal'
import ArchiveDiscussionReplyModal from './modals/ArchiveDiscussionReplyModal'
import DocumentRow from 'Document/Resources/assets/js/components/DocumentRow'

export default {
  name: 'DiscussionPost',
  components: {
    Popper,
    DocumentRow,
    ArchiveDiscussionPostModal,
    ArchiveDiscussionReplyModal,
  },
  props: {
    postUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentTime: false,
      currentDate: false,
      isReplying: false,
      messageShow: false,
      loaded: false,
      repliesLoaded: false,
      replyPage: 1,
      reply: {
        body: '',
      },
      redactorConfig: {
        buttons: ['bold', 'italic', 'ul', 'ol', 'link', 'image'],
        imageUpload: this.$urlGenerator.active('api/v1/images/redactor'),
        imageUploadParam: 'images',
        plugins: ['video'],
        minHeight: '300px',
      },
      sort_direction: 'asc',
    }
  },
  computed: {
    ...mapState({
      count: state => state.discussion.replies.data.length,
      totalCount: state => _.get(state.discussion.replies, 'meta.pagination.total', 0),
      post: state => state.discussion.post,
      replies: state => state.discussion.replies.data,
      replyPagination: state => state.discussion.replies.meta.pagination,
    }),
    editPostUrl() {
      return this.$urlGenerator.active(`discussions/${this.post.uuid}/edit`)
    },
    repliesCount() {
      return this.post.meta.reply_count === 1
        ? '1 Reply'
        : `${this.post.meta.reply_count} Replies`
    },
    authorAvatar() {
      return this.post.creator._type === 'User'
        ? this.post.creator.attributes.avatar.small
        : this.post.creator.attributes.small_avatar_url
    },
    creatorRole() {
      return this.post.creator._type === 'User'
        ? 'Super Admin'
        : _.get(this.post.creator, 'role.attributes.title', false)
    },
    documents() {
      return _.get(this.post, 'folder.documents.data', [])
    },
    statusClass(){
      switch (this.post.attributes.status) {
        case 'Declined':
          return 'text-red'
        case 'Approved':
          return 'text-green'
        case 'Pending':
          return 'text-blue'
        default:
          return 'text-grey-darker'
      }
    },
  },
  watch: {
    isReplying() {
      if (this.isReplying) {
        if (this.$refs.reply) {
          this.$nextTick(() => { this.$refs.reply.focus() })
        }
      }
    },
    sort_direction() {
      this.fetchReplies(true)
    },
  },
  mounted() {
    this.updateTime()
    this.fetchPost()
    this.fetchReplies()
  },
  methods: {
    ...mapActions([
      'discussionRemovePost',
      'discussionGetPost',
      'discussionGetReplies',
      'discussionCreateReply',
      'discussionApprovePost',
      'discussionRejectPost',
    ]),
    cancel() {
      this.reply.body = ''

      this.toggleReply()
    },
    openArchivePostModal() {
      this.$store.commit('discussionArchivePostModalOpen', this.post)

      this.$refs.popper.showPopper = false
    },
    toggleReply() {
      this.isReplying = !this.isReplying
    },
    onReplyPageChange(page) {
      this.replyPage = page

      this.fetchReplies(true)
    },
    archive() {
      this.discussionRemovePost({
        postUuid: this.post.uuid,
        redirect: this.post.category._links.frontend.self,
      })
    },
    approve() {
      this.discussionApprovePost(this.post)
    },
    reject() {
      this.discussionRejectPost(this.post)
    },
    submit() {
      if (!this.reply.body.length) {
        return
      }
      this.discussionCreateReply({
        postUuid: this.post.uuid,
        params: this.reply,
      }).then((response) => {
        this.cancel()
      })
    },
    updateTime() {
      this.currentTime = this.moment().format('h:mm A')
      this.currentDate = this.moment().format('M/D/YYYY')

      setInterval(() => {
        this.currentTime = this.moment().format('h:mm A')
        this.currentDate = this.moment().format('M/D/YYYY')
      }, 1000)
    },
    fetchPost() {
      this.loaded = false

      this.discussionGetPost({
        postUuid: this.postUuid,
        params: {
          include: 'category,folder.documents',
        },
      }).then(() => {
        this.loaded = true
      })
    },
    fetchReplies(shouldForce = false) {
      this.repliesLoaded = false

      this.discussionGetReplies({
        postUuid: this.postUuid,
        params: {
          page: this.replyPage,
          per_page: 20,
          include: 'creator.role',
          sort_direction: this.sort_direction,
        },
        force: shouldForce,
      }).then((response) => {
        this.repliesLoaded = true
      })
    },
  },
}
</script>
